import { useState, useRef, useEffect } from 'react';
import {
  Modal,
  Button,
  Group,
  Text,
  Stack,
  Badge,
  Select,
  ActionIcon,
  Tooltip,
  Paper,
  FileButton,
  Image,
  Collapse,
  Box,
  TextInput,
  Textarea,
} from '@mantine/core';
import { IconEdit, IconTrash, IconChevronDown, IconChevronUp, IconUpload, IconCheck, IconX } from '@tabler/icons-react';
import { BetSlipWithSelections, BetSlipStatus } from '../types/betslip';
import { updateBetSlipStatus, deleteBetSlip, updateBetSlip } from '../services/api';

const statusColors: Record<BetSlipStatus, string> = {
  pending: 'yellow',
  won: 'green',
  lost: 'red',
  push: 'gray',
  cancelled: 'gray',
};

interface BetSlipDetailsProps {
  betSlip: Partial<BetSlipWithSelections>;
  opened: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

export function BetSlipDetails({ betSlip, opened, onClose, onUpdate }: BetSlipDetailsProps) {
  console.log('BetSlipDetails - received betSlip:', betSlip);

  const [loading, setLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<BetSlipStatus>(
    betSlip.status as BetSlipStatus || 'pending'
  );
  const [isEditing, setIsEditing] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const resetRef = useRef<() => void>(null);
  const [editedData, setEditedData] = useState({
    casinoName: betSlip.casinoName || '',
    notes: betSlip.notes || '',
    eventDateTime: betSlip.eventDateTime || undefined,
  });
  const [currentBetSlip, setCurrentBetSlip] = useState(betSlip);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    console.log('BetSlipDetails - useEffect - betSlip:', betSlip);
    setCurrentStatus(betSlip.status as BetSlipStatus || 'pending');
    setEditedData({
      casinoName: betSlip.casinoName || '',
      notes: betSlip.notes || '',
      eventDateTime: betSlip.eventDateTime || undefined,
    });
    setCurrentBetSlip(betSlip);
    setImageError(false);
  }, [betSlip]);

  const handleStatusChange = async (newStatus: BetSlipStatus) => {
    if (!betSlip.id || !newStatus) return;

    try {
      setLoading(true);
      const result = await updateBetSlipStatus(betSlip.id, newStatus);
      setCurrentStatus(newStatus);
      setCurrentBetSlip(result);
      onUpdate();
    } catch (error) {
      console.error('Error updating bet slip status:', error);
      // Revert to previous status on error
      setCurrentStatus(betSlip.status as BetSlipStatus);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (event: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent event bubbling
    event.stopPropagation();
    
    console.log('handleDelete - betSlip:', betSlip);
    
    if (!betSlip.id) {
      console.error('No bet slip ID provided for deletion');
      return;
    }

    const confirmed = window.confirm('Are you sure you want to delete this bet slip?');
    if (!confirmed) return;

    try {
      console.log('Deleting bet slip:', betSlip.id);
      setLoading(true);
      await deleteBetSlip(betSlip.id);
      console.log('Bet slip deleted successfully');
      onClose();
      onUpdate();
    } catch (error) {
      console.error('Error deleting bet slip:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file: File | null) => {
    if (!file || !betSlip.id) return;

    try {
      setLoading(true);
      const result = await updateBetSlip(betSlip.id, {
        notes: editedData.notes,
        status: currentStatus,
      }, file);
      setCurrentBetSlip(result);
      setImageError(false);
      onUpdate();
      if (resetRef.current) {
        resetRef.current();
      }
    } catch (error) {
      console.error('Error uploading new image:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!betSlip.id) return;

    try {
      setLoading(true);
      console.log('Saving changes:', editedData);
      
      // Only send fields that have changed
      const updates: Record<string, any> = {};
      
      if (editedData.casinoName !== betSlip.casinoName) {
        updates.casinoName = editedData.casinoName;
      }
      if (editedData.notes !== betSlip.notes) {
        updates.notes = editedData.notes;
      }
      if (editedData.eventDateTime?.toISOString() !== betSlip.eventDateTime?.toString()) {
        updates.eventDateTime = editedData.eventDateTime;
      }
      if (currentStatus !== betSlip.status) {
        updates.status = currentStatus;
        if (currentStatus !== 'pending') {
          updates.settledDateTime = new Date();
        }
      }

      // Update bet slip with edited data
      const result = await updateBetSlip(betSlip.id, updates);
      console.log('Update result:', result);
      
      // Update local state with the result
      setCurrentBetSlip(result);
      
      // Exit edit mode
      setIsEditing(false);
      
      // Trigger refresh of parent component
      onUpdate();
    } catch (error) {
      console.error('Error updating bet slip:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle image error
  const handleImageError = () => {
    console.error('Image failed to load:', currentBetSlip.imageUrl);
    setImageError(true);
  };

  // Decode the image URL to handle special characters
  const decodedImageUrl = currentBetSlip.imageUrl ? decodeURIComponent(currentBetSlip.imageUrl) : '';
  console.log('Decoded image URL:', decodedImageUrl);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text size="lg" weight={500}>Bet Slip Details</Text>}
      size="lg"
    >
      <Stack spacing="md">
        <Group position="apart">
          <Group spacing="xs">
            <Text weight={500}>Ticket ID: {currentBetSlip.ticketId}</Text>
            {currentStatus && (
              <Badge color={statusColors[currentStatus]}>
                {currentStatus.toUpperCase()}
              </Badge>
            )}
          </Group>
          <Group spacing="xs">
            {isEditing ? (
              <>
                <Tooltip label="Save changes">
                  <ActionIcon color="green" variant="light" onClick={handleSave} loading={loading}>
                    <IconCheck size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Cancel">
                  <ActionIcon color="gray" variant="light" onClick={() => {
                    setIsEditing(false);
                    setEditedData({
                      casinoName: currentBetSlip.casinoName || '',
                      notes: currentBetSlip.notes || '',
                      eventDateTime: currentBetSlip.eventDateTime || undefined,
                    });
                  }}>
                    <IconX size={16} />
                  </ActionIcon>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip label="Edit bet slip">
                  <ActionIcon color="blue" variant="light" onClick={() => setIsEditing(true)}>
                    <IconEdit size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Delete bet slip">
                  <ActionIcon 
                    color="red" 
                    variant="light" 
                    onClick={handleDelete}
                    loading={loading}
                    disabled={loading}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Group>
        </Group>

        <Group grow>
          <Stack spacing={4}>
            <Text size="sm" color="dimmed">Casino</Text>
            {isEditing ? (
              <TextInput
                value={editedData.casinoName}
                onChange={(e) => setEditedData({ ...editedData, casinoName: e.target.value })}
                placeholder="Casino name"
                size="sm"
              />
            ) : (
              <Text>{currentBetSlip.casinoName || 'N/A'}</Text>
            )}
          </Stack>
          <Stack spacing={4}>
            <Text size="sm" color="dimmed">Event Date/Time</Text>
            {isEditing ? (
              <Group grow>
                <input
                  type="date"
                  value={editedData.eventDateTime ? new Date(editedData.eventDateTime).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const date = new Date(e.target.value);
                    if (!isNaN(date.getTime())) {
                      const currentTime = editedData.eventDateTime ? new Date(editedData.eventDateTime) : new Date();
                      date.setHours(currentTime.getHours(), currentTime.getMinutes());
                      setEditedData({ ...editedData, eventDateTime: date });
                    }
                  }}
                  style={{ padding: '6px', border: '1px solid #ced4da', borderRadius: '4px' }}
                />
                <input
                  type="time"
                  value={editedData.eventDateTime ? new Date(editedData.eventDateTime).toTimeString().substring(0,5) : ''}
                  onChange={(e) => {
                    const currentDate = editedData.eventDateTime ? new Date(editedData.eventDateTime) : new Date();
                    const [hours, minutes] = e.target.value.split(':').map(Number);
                    currentDate.setHours(hours, minutes);
                    setEditedData({ ...editedData, eventDateTime: currentDate });
                  }}
                  style={{ padding: '6px', border: '1px solid #ced4da', borderRadius: '4px', marginLeft: '8px' }}
                />
              </Group>
            ) : (
              <Text>
                {currentBetSlip.eventDateTime
                  ? new Date(currentBetSlip.eventDateTime).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    })
                  : 'N/A'}
              </Text>
            )}
          </Stack>
        </Group>

        <Group grow>
          <Stack spacing={4}>
            <Text size="sm" color="dimmed">Wager Amount</Text>
            <Text>${(currentBetSlip.wagerAmount || 0).toFixed(2)}</Text>
          </Stack>
          <Stack spacing={4}>
            <Text size="sm" color="dimmed">Potential Payout</Text>
            <Text>${(currentBetSlip.potentialPayout || 0).toFixed(2)}</Text>
          </Stack>
        </Group>

        <Paper withBorder p="md">
          <Stack spacing="md">
            <Text weight={500}>Update Status</Text>
            <Select
              value={currentStatus}
              onChange={(value) => value && handleStatusChange(value as BetSlipStatus)}
              data={[
                { value: 'pending', label: 'Pending' },
                { value: 'won', label: 'Won' },
                { value: 'lost', label: 'Lost' },
                { value: 'push', label: 'Push' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
              disabled={loading}
              placeholder="Select status"
              clearable={false}
              required
            />
          </Stack>
        </Paper>

        {currentBetSlip.selections && currentBetSlip.selections.length > 0 && (
          <Stack spacing="xs">
            <Text weight={500}>Selections</Text>
            {currentBetSlip.selections.map((selection, index) => (
              <Paper key={index} withBorder p="md">
                <Stack spacing="xs">
                  <Group position="apart">
                    <Text weight={500}>Selection {index + 1}</Text>
                    <Badge>{selection.marketType}</Badge>
                  </Group>
                  <Group grow>
                    <Stack spacing={2}>
                      <Text size="sm" color="dimmed">Sport</Text>
                      <Text>{selection.sport || 'N/A'}</Text>
                    </Stack>
                    <Stack spacing={2}>
                      <Text size="sm" color="dimmed">League</Text>
                      <Text>{selection.league || 'N/A'}</Text>
                    </Stack>
                  </Group>
                  <Group grow>
                    <Stack spacing={2}>
                      <Text size="sm" color="dimmed">Event Date/Time</Text>
                      <Text>
                        {selection.eventDateTime
                          ? new Date(selection.eventDateTime).toLocaleString(undefined, {
                              dateStyle: 'medium',
                              timeStyle: 'short'
                            })
                          : 'N/A'}
                      </Text>
                    </Stack>
                  </Group>
                  <Group grow>
                    <Stack spacing={2}>
                      <Text size="sm" color="dimmed">Teams</Text>
                      <Text>{selection.homeTeam} vs {selection.awayTeam}</Text>
                    </Stack>
                    <Stack spacing={2}>
                      <Text size="sm" color="dimmed">Selected</Text>
                      <Text>{selection.selectedTeam}</Text>
                    </Stack>
                  </Group>
                  <Group grow>
                    <Stack spacing={2}>
                      <Text size="sm" color="dimmed">Line</Text>
                      <Text>{selection.line || 'N/A'}</Text>
                    </Stack>
                    <Stack spacing={2}>
                      <Text size="sm" color="dimmed">Odds</Text>
                      <Text>{selection.odds}</Text>
                    </Stack>
                  </Group>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        {isEditing && (
          <Paper withBorder p="md">
            <Stack spacing="xs">
              <Text weight={500}>Notes</Text>
              <Textarea
                value={editedData.notes}
                onChange={(e) => setEditedData({ ...editedData, notes: e.target.value })}
                placeholder="Add notes about this bet slip"
                minRows={3}
              />
            </Stack>
          </Paper>
        )}

        {decodedImageUrl && !imageError && (
          <Paper withBorder>
            <Group 
              position="apart" 
              p="md" 
              style={{ cursor: 'pointer' }}
              onClick={() => setShowImage(!showImage)}
            >
              <Text weight={500}>Bet Slip Image</Text>
              <ActionIcon>
                {showImage ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
              </ActionIcon>
            </Group>
            <Collapse in={showImage}>
              <Box p="md">
                <Image
                  src={decodedImageUrl}
                  alt="Bet slip"
                  fit="contain"
                  height={300}
                  withPlaceholder
                  onError={handleImageError}
                />
                {isEditing && (
                  <Group position="center" mt="md">
                    <FileButton
                      resetRef={resetRef}
                      accept="image/*"
                      onChange={handleImageUpload}
                    >
                      {(props) => (
                        <Button
                          variant="light"
                          leftIcon={<IconUpload size={16} />}
                          loading={loading}
                          {...props}
                        >
                          Upload New Image
                        </Button>
                      )}
                    </FileButton>
                  </Group>
                )}
              </Box>
            </Collapse>
          </Paper>
        )}

        <Group position="right" mt="md">
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}